import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import { T } from "services/lang/LanguageService";
import ManageDatasetMappers
    from "components/dashboard/dataset-window/datasets-menu/dataset-mappers/ManageDatasetMappers";
import FindAndReplaceDialog
    from "components/dashboard/dataset-window/datasets-menu/find-and-replace-dialog/FindAndReplaceDialog";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Menu} from "primereact/menu";
import React, {useRef, useState} from "react";
import {ChronoUnitLegacy} from "utils/time-utils";
import {useDeleteDataset, useHardDeleteDataset} from "api/api-hooks/datasets-hooks";
import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {BaseDatasetDTO} from "api/model/dataset/BaseDatasetDTO";
import {easasConfirmDialog} from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import DatasetUsageSearchDialog from "components/fragments/dataset-usage-search/DatasetUsageSearchDialog";
import AddDataDialog from "./add-data-dialog/AddDataDialog";
import ClearDatasetDialog from "./clear-dataset-dialog/ClearDatasetDialog";
import CreateDatasetDialog from "./create-dataset-dialog/CreateDatasetDialog";
import EditDatasetDialog from "./edit-dataset-dialog/EditDatasetDialog";
import DatasetGroupsSettings from "./dataset-groups/DatasetGroupsSettings";
import {useHasPermissions} from "../../../../hooks/useHasPermissions";

interface IDatasetsMenuProps {
    selectedDatasets: Array<DatasetInfo>,
    setSelectedDatasets: (arr: Array<DatasetInfo>) => void
}

const DatasetsMenu = ({selectedDatasets, setSelectedDatasets}: IDatasetsMenuProps) => {
    const [createDatasetDialogVisibility, setCreateDialogVisibility] = useState<boolean>(false);
    const [clearDataDialogVisibility, setClearDataDialogVisibility] = useState<boolean>(false);
    const [addDataDialogVisibility, setAddDataDialogVisibility] = useState<boolean>(false);
    const [editDatasetDialogVisibility, setEditDatasetDialogVisibility] = useState<boolean>(false);
    const [findAndReplaceDialogVisibility, setFindAndReplaceDialogVisibility] = useState<boolean>(false);
    const [manageMappersDialogVisibility, setManageMappersDialogVisibility] = useState<boolean>(false);
    const [searchDatasetsDialogVisibility, setSearchDatasetsDialogVisibility] = useState(false);
    const [datasetGroups, setDatasetGroups] = useState({ visibility: false, set: false });

    const hasSoftDeletePerm = useHasPermissions("DATASET_DELETE_SOFT");
    const hasHardDeletePerm = useHasPermissions("DATASET_DELETE_HARD");

    const { mutateAsync: softDeleteMut } = useDeleteDataset();
    const { mutateAsync: hardDeleteMut } = useHardDeleteDataset();

    const menu = useRef<Menu | null>(null);
    const items = [
        {
            label: T("label.dataset-options"),
            items: [
                {
                    label: T("action.create-dataset"),
                    icon: "pi pi-plus-circle",
                    command: () => {
                        setCreateDialogVisibility(true);
                    }
                },
                {
                    label: T("label.search-datasets-usage"),
                    icon: "pi pi-search",
                    command: () => {
                        setSearchDatasetsDialogVisibility(true);
                    }
                },
                {
                    label: T("action.edit-dataset"),
                    icon: "pi pi-pencil",
                    command: () => {
                        setEditDatasetDialogVisibility(true);
                    },
                    disabled: selectedDatasets.length === 0
                },
                {
                  label: T("action.set-group"),
                  icon: "pi pi-sitemap",
                  command: () => {
                    setDatasetGroups({ visibility: true, set: true });

                  },
                  disabled: selectedDatasets.length === 0
                },
                {
                    label: T("action.find-and-replace"),
                    icon: "pi pi-search",
                    command: () => {
                        setFindAndReplaceDialogVisibility(true);
                    },
                    disabled: selectedDatasets.length === 0
                },
                {
                    label: T("action.delete-dataset"),
                    icon: "pi pi-trash",
                    command: () => {
                        confirmDelete("SOFT");
                    },
                    disabled: selectedDatasets.length === 0 || !hasSoftDeletePerm
                },
                {
                    label: T("action.hard-delete-dataset"),
                    icon: "pi pi-trash",
                    command: () => {
                        confirmDelete("HARD");
                    },
                    disabled: selectedDatasets.length === 0 || !hasHardDeletePerm
                }
            ]
        },
        {
           label: T("label.dataset-options"),
           items: [
                {
                    label: T("action.add-data"),
                    icon: "pi pi-plus",
                    command: () => {
                        setAddDataDialogVisibility(true);
                    },
                    disabled: selectedDatasets.length !== 1
                },
                {
                    label: T("action.clear-data"),
                    icon: "pi pi-minus-circle",
                    command: () => {
                        setClearDataDialogVisibility(true);
                    },
                    disabled: selectedDatasets.length === 0
                }
            ]
        },
        {
          label: T("word.settings"),
          items: [
              {
                  label: T("action.manage-mappings"),
                  icon: "pi pi-pencil",
                  command: () => setManageMappersDialogVisibility(true),
              },
              {
                  label: T("label.dataset-groups"),
                  icon: "pi pi-users",
                  command: () => setDatasetGroups({ visibility: true, set: false }),
              }
          ]
        },


    ];

    const confirmDelete = (type: "SOFT" | "HARD") => {
      easasConfirmDialog({
        header: T("confirm-dialog.report-table.delete-confirmation"),
        message: `${T("confirm-dialog.datasets.do-you-want-to-delete-these-datasets")} ${type === "HARD" ? " This action deletes data and is irreversible." : ""}`,
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        confirmedCallback: () => deleteHandler(type),
      });
    };

    const deleteHandler = async (type: "SOFT" | "HARD") => {
      const identifiers = selectedDatasets.map(dataset => dataset.identifier);
      const deleteMut = type === "SOFT" ? softDeleteMut : hardDeleteMut;
      const results = await Promise.allSettled(identifiers.map(id => deleteMut(id!)));

      if (results.every(result => result.status === "fulfilled")) {
        easasAlert({
          summary: T("alert.success"),
          detail: T("alert.datasets.datasets-were-deleted-successfully"),
          severity: "success",
        });
      } else {
        easasAlert({
          summary: T("alert.error"),
          detail: T("error.dataset-failed-to-delete"),
          severity: "error",
        });
      }
      setSelectedDatasets([]);
    }

    return (
      <>
        <div>
          <Menu model={items} popup ref={menu} id="popup_menu" />
          <Button
            className="p-button-outlined p-button-secondary"
            icon="pi pi-bars"
            onClick={event => menu.current?.toggle(event)}
            aria-controls="popup_menu"
            aria-haspopup
          />
        </div>
        <DatasetUsageSearchDialog visible={searchDatasetsDialogVisibility} closeDialog={() => setSearchDatasetsDialogVisibility(false)} />
        <Dialog
          style={{ width: "min(100vw, 800px)", minHeight: "250px" }}
          header={T("action.create-new-dataset")}
          visible={createDatasetDialogVisibility}
          onHide={() => setCreateDialogVisibility(false)}
          resizable
          blockScroll
          modal={false}
        >
          <CreateDatasetDialog setCreateDialogVisibility={setCreateDialogVisibility} />
        </Dialog>
        <Dialog
          style={{ width: "min(100vw, 1200px)", height: "400x" }}
          header={T("action.edit-dataset")}
          visible={editDatasetDialogVisibility}
          onHide={() => setEditDatasetDialogVisibility(false)}
          resizable
          modal={false}
        >
          <EditDatasetDialog
            setEditDatasetDialogVisibility={setEditDatasetDialogVisibility}
            selectedDatasets={selectedDatasets}
            setSelectedDatasets={setSelectedDatasets}
          />
        </Dialog>
        <Dialog
          style={{ width: "min(100vw, 400px)", height: "400x" }}
          header={T("action.edit-dataset")}
          visible={findAndReplaceDialogVisibility}
          onHide={() => setFindAndReplaceDialogVisibility(false)}
          resizable
        >
          <FindAndReplaceDialog
            setFindAndReplaceDialogVisibility={setFindAndReplaceDialogVisibility}
            selectedDatasets={selectedDatasets}
            setSelectedDatasets={setSelectedDatasets}
          />
        </Dialog>
        <Dialog
          style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
          header={T("action.manage-mappers")}
          visible={manageMappersDialogVisibility}
          onHide={() => setManageMappersDialogVisibility(false)}
          resizable
        >
          <ManageDatasetMappers />
        </Dialog>
        <Dialog
          style={{ width: "min(100vw, 800px)", height: "600px" }}
          header={T("label.dataset-groups")}
          visible={datasetGroups.visibility}
          onHide={() => setDatasetGroups(prev => ({ ...prev, visibility: false }))}
          resizable
        >
          <DatasetGroupsSettings
            selectedDatasets={selectedDatasets as BaseDatasetDTO[]}
            set={datasetGroups.set}
            closeDialog={() => setDatasetGroups(prev => ({ ...prev, visibility: false }))}
          />
        </Dialog>
        <Dialog
          style={{ width: "min(100vw, 400px)", height: "400x" }}
          header={T("action.clear-dataset-data")}
          visible={clearDataDialogVisibility}
          onHide={() => setClearDataDialogVisibility(false)}
          resizable={false}
          modal
        >
          <ClearDatasetDialog
            setClearDataDialogVisibility={setClearDataDialogVisibility}
            selectedDatasets={selectedDatasets}
          />
        </Dialog>
        <Dialog
          onHide={() => setAddDataDialogVisibility(false)}
          style={{ width: "min(100vw, 600px)", height: "400x" }}
          visible={addDataDialogVisibility}
          header={T("action.add-data-to-dataset")}
          modal
        >
          <AddDataDialog
            selectedDatasets={selectedDatasets}
            firstLoadDate={new Date()}
            dateFrequency={ChronoUnitLegacy.MINUTES}
            closeDialog={() => setAddDataDialogVisibility(false)}
          />
        </Dialog>
      </>
    );
};

export default DatasetsMenu;